import {
  Box,
  Flex,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import React from 'react';

import type { Transaction } from 'types/api/transaction';

import config from 'configs/app';
import getValueWithUnit from 'lib/getValueWithUnit';
import { currencyUnits } from 'lib/units';
import Skeleton from 'ui/shared/chakra/Skeleton';
import AddressEntity from 'ui/shared/entities/address/AddressEntity';
import TxEntity from 'ui/shared/entities/tx/TxEntity';
import TxStatus from 'ui/shared/statusTag/TxStatus';
import TimeAgoWithTooltip from 'ui/shared/TimeAgoWithTooltip';
import TxFee from 'ui/shared/tx/TxFee';
import TxAdditionalInfo from 'ui/txs/TxAdditionalInfo';

type Props = {
  tx: Transaction;
  isLoading?: boolean;
};

const LatestTxsItem = ({ tx, isLoading }: Props) => {
  const dataTo = tx.to ? tx.to : tx.created_contract;
  const borderColor = useColorModeValue('#E9ECEF', '#222222');
  const labelColor = useColorModeValue('#616161', '#999999');
  const valueColor = useColorModeValue('#000000', '#FFFFFF');
  const timeColor = useColorModeValue('#999999', '#616161');

  const addressSx = {
    a: { color: valueColor, _hover: { color: valueColor, textDecoration: 'none' } },
  };

  return (
    <Box
      width="100%"
      borderBottom="1px solid"
      borderColor={ borderColor }
      _last={{ borderBottom: 'none' }}
      px="16px"
      py="16px"
      display={{ base: 'block', lg: 'none' }}
    >
      <Flex direction="column" gap="8px">

        { /* Row 1: Status badge + date/time */ }
        <Flex justifyContent="space-between" alignItems="center">
          <TxStatus
            status={ tx.status }
            errorText={ tx.status === 'error' ? tx.result : undefined }
            isLoading={ isLoading }
          />
          <TimeAgoWithTooltip
            timestamp={ tx.timestamp }
            enableIncrement
            isLoading={ isLoading }
            color={ timeColor }
            fontWeight={ 500 }
            fontSize="14px"
            fontFamily="'Space Grotesk', var(--chakra-fonts-body)"
          />
        </Flex>

        { /* Row 2: Info icon + tx hash */ }
        <Flex alignItems="center" gap="6px">
          <TxAdditionalInfo tx={ tx } isMobile isLoading={ isLoading }/>
          <TxEntity
            isLoading={ isLoading }
            hash={ tx.hash }
            fontWeight={ 500 }
            fontSize="14px"
            lineHeight="17.5px"
            fontFamily="'Oxanium', var(--chakra-fonts-body)"
            color={ valueColor }
            truncation="constant"
            _hover={{ color: valueColor, textDecoration: 'none' }}
          />
        </Flex>

        { /* Row 3: From address */ }
        <Flex alignItems="center" gap="8px" minW={ 0 }>
          <Text
            color={ labelColor }
            fontSize="14px"
            fontWeight={ 500 }
            lineHeight="17.86px"
            fontFamily="'Space Grotesk', var(--chakra-fonts-body)"
            flexShrink={ 0 }
          >
            From
          </Text>
          <AddressEntity
            address={ tx.from }
            isLoading={ isLoading }
            noCopy
            truncation="constant"
            fontSize="14px"
            lineHeight="17.5px"
            fontWeight={ 500 }
            fontFamily="'Oxanium', var(--chakra-fonts-body)"
            sx={ addressSx }
          />
        </Flex>

        { /* Row 4: To address */ }
        { dataTo && (
          <Flex alignItems="center" gap="8px" minW={ 0 }>
            <Text
              color={ labelColor }
              fontSize="14px"
              fontWeight={ 500 }
              lineHeight="17.86px"
              fontFamily="'Space Grotesk', var(--chakra-fonts-body)"
              flexShrink={ 0 }
            >
              To
            </Text>
            <AddressEntity
              address={ dataTo }
              isLoading={ isLoading }
              noCopy
              truncation="constant"
              fontSize="14px"
              lineHeight="17.5px"
              fontWeight={ 500 }
              fontFamily="'Oxanium', var(--chakra-fonts-body)"
              sx={ addressSx }
            />
          </Flex>
        ) }

        { /* Row 5: Value | Fee */ }
        <Flex justifyContent="space-between" alignItems="center">
          { !config.UI.views.tx.hiddenFields?.value && (
            <Skeleton isLoaded={ !isLoading }>
              <Flex alignItems="center" gap="4px">
                <Text
                  color={ labelColor }
                  fontSize="14px"
                  fontWeight={ 500 }
                  lineHeight="17.86px"
                  fontFamily="'Space Grotesk', var(--chakra-fonts-body)"
                >
                  Value
                </Text>
                <Text
                  color={ valueColor }
                  fontSize="14px"
                  lineHeight="17.5px"
                  fontWeight={ 500 }
                  fontFamily="'Oxanium', var(--chakra-fonts-body)"
                >
                  { getValueWithUnit(tx.value).dp(5).toFormat() } { currencyUnits.ether }
                </Text>
              </Flex>
            </Skeleton>
          ) }
          { !config.UI.views.tx.hiddenFields?.tx_fee && (
            <Skeleton isLoaded={ !isLoading }>
              <Flex alignItems="center" gap="4px">
                <Text
                  color={ labelColor }
                  fontSize="14px"
                  fontWeight={ 500 }
                  lineHeight="17.86px"
                  fontFamily="'Space Grotesk', var(--chakra-fonts-body)"
                >
                  Fee
                </Text>
                <TxFee
                  tx={ tx }
                  accuracy={ 5 }
                  color={ valueColor }
                  fontSize="14px"
                  lineHeight="17.5px"
                  fontWeight={ 500 }
                  fontFamily="'Oxanium', var(--chakra-fonts-body)"
                />
              </Flex>
            </Skeleton>
          ) }
        </Flex>

      </Flex>
    </Box>
  );
};

export default React.memo(LatestTxsItem);
